<script lang="ts" setup>
import isNull from 'lodash/isNull'
import type { PlayerRoster, Player, Image } from '~/types'
import { SocialNetwork } from '~/enums/social-network'

interface Props {
  player: Player.Model
  playerRoster?: Maybe<PlayerRoster.Model>
}

const props = defineProps<Props>()

const {
  age,
  birthPlace,
  birthDate,
  college,
  facebookLink,
  fullName,
  heightFeet,
  instagramLink,
  jerseyNumber,
  position,
  photo,
  tiktokLink,
  weight,
  xLink,
  youtubeLink,
} = usePlayer(props.player)

const {
  playerRosterPhoto,
} = usePlayerRoster(props.playerRoster)

const { dateLuxonToFormat: date } = useDate(birthDate)

const currentPhoto = computed<Maybe<Image>>(() => playerRosterPhoto.value ?? photo.value)
</script>

<template>
  <section class="player-bio">
    <div class="player-bio-hero">
      <Wrapper>
        <div class="player-bio-hero__content">
          <div
            v-if="currentPhoto"
            class="player-bio-hero__photo"
          >
            <UIImage
              :image="currentPhoto"
              class="player-bio-hero__photo-image"
            />
          </div>
          <div class="player-bio-hero__info">
            <UIDynamicHeading
              level="1"
              as="h1"
              class="player-bio-hero__name"
            >
              <template v-if="!isNull(jerseyNumber)">
                #{{ jerseyNumber }}
              </template> {{ fullName }}
            </UIDynamicHeading>
            <div class="player-bio-hero__meta">
              <PlayerField
                label="Position"
                :value="position"
                class="player-bio-hero__field"
              />
              <PlayerField
                label="Height"
                :value="heightFeet"
                class="player-bio-hero__field"
              />
              <PlayerField
                label="Weight"
                :value="weight"
                class="player-bio-hero__field"
              />
              <PlayerField
                label="College"
                :value="college"
                class="player-bio-hero__field"
              />
              <PlayerField
                label="Hometown"
                :value="birthPlace"
                class="player-bio-hero__field"
              />
              <PlayerField
                v-if="age"
                label="Age"
                :value="`${age} (${date})`"
                class="player-bio-hero__field"
              />
            </div>

            <div class="player-bio-hero__socials">
              <SocialLink
                v-if="xLink"
                :href="xLink"
                :type="SocialNetwork.X"
              />
              <SocialLink
                v-if="instagramLink"
                :href="instagramLink"
                :type="SocialNetwork.Instagram"
              />
              <SocialLink
                v-if="tiktokLink"
                :href="tiktokLink"
                :type="SocialNetwork.TikTok"
              />
              <SocialLink
                v-if="youtubeLink"
                :href="youtubeLink"
                :type="SocialNetwork.YouTube"
              />
              <SocialLink
                v-if="facebookLink"
                :href="facebookLink"
                :type="SocialNetwork.Facebook"
              />
            </div>
          </div>
        </div>
      </Wrapper>
    </div>
    <slot />
  </section>
</template>

<style lang="scss" scoped>
.player-bio {
  &-hero {
    @apply mb-[20px];
    @apply md:mb-[40px];
    @apply xl:mb-[60px];

    &__banner {
      @apply w-full h-[250px];
      @apply md:h-[400px];
      @apply xl:h-[600px];

      &-image {
        @apply w-full h-full object-cover object-top;
      }
    }

    &__content {
      @apply py-[50px] flex flex-col items-center;
      @apply md:flex-row;
    }

    &__photo {
      @apply w-[260px] h-[300px] border-[4px] border-primary shrink-0 mx-auto mb-[30px];
      @apply md:mr-[40px] md:ml-0 md:mb-0;
      @apply lg:w-[300px] lg:h-[360px];
      @apply xl:w-[340px] xl:h-[400px] xl:mr-[60px];

      &-image {
        @apply w-full h-full object-cover object-top;
      }
    }

    &__info {
      @apply grow flex flex-col items-center text-center gap-[40px];
      @apply md:items-start md:text-left;
    }

    &__name {
      @apply text-primary;
    }

    &__meta {
      @apply flex flex-wrap items-center justify-center gap-x-[30px] gap-y-[30px] text-primary;
      @apply md:justify-start;
      @apply xl:gap-x-[50px];
    }

    &__socials {
      @apply flex items-center gap-[10px];
    }
  }
}
</style>
